import * as React from 'react';
import {
    Alert, Box, Breadcrumbs, Button, Card, CardContent, Chip, CircularProgress, FormControlLabel, Grid, Link as MuiLink, Stack,
    Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AI_LABELS, createAiAgent, deleteAiAgent, getAiAgent, updateAiAgent } from '../../Api';
import {
    AI_AGENT_KINDS, AI_DATA_CLASSES, AI_STATUSES, AiAgentDto, AiAgentKind, AiDataClass, AiJsonObject, AiParams, AiStatus,
    AiUpsertAgentRequest,
} from '../../models/dtos/AiDto';
import { ConfirmDialog } from '../../components/betupFeed/BetupFeedShared';
import { AiExportButton } from '../../components/ai/AiBundleExport';
import {
    AiActionError, AiCodeEditor, AiEffectiveParams, AiErrorAlert, AiModelPickers, aiLoadErrorText, EnumSelect, formatAiDate, PARAMS_HELP,
    parseJsonText, parseOptionalNumber, textError, toAiActionError, toJsonText, useAiCatalog,
} from '../../components/ai/AiShared';
import {
    AgentDelegationTab, AgentEffectiveToolsTab, AgentTestRunTab, AgentToolsTab,
} from '../../components/ai/AiAgentTabs';
import AiRunsGrid from '../../components/ai/AiRunsGrid';
import AgentConversationSettings from '../../components/ai/AiAgentConversationSettings';
import AgentPromptContext, { AiCacheMode, ContextToolForm, contextToolsFromParams, contextToolsToParams } from '../../components/ai/AiAgentPromptContext';
import AgentWebTools, { EMPTY_WEB_TOOLS, webToolsFromParams, webToolsToParams, WebToolsForm } from '../../components/ai/AiAgentWebTools';

export interface AgentForm {
    code: string;
    description: string;
    kind: AiAgentKind;
    ownerService: string;
    dataClass: AiDataClass;
    status: AiStatus;
    modelId: number | null;
    fallbackModelId: number | null;
    providerKeyId: number | null;
    maxOutputTokens: string;
    maxTurns: string;
    runTimeoutS: string;
    maxConversationRuns: string;
    /** Only opted-in agents are counted against (and blocked by) per-user budgets. */
    countsTowardUserBudgets: boolean;
    /** params.cache: what each call caches (1 hour); agents default to SYSTEM. */
    cache: AiCacheMode;
    /** params.contextTools: MCP tools appended to the system prompt before each run. */
    contextTools: ContextToolForm[];
    /** params.serverTools: web search / web fetch run by the provider. */
    webTools: WebToolsForm;
    /** params.conversation.* — blank = the hub's default. */
    contextTokens: string;
    replayToolResultChars: string;
    otherConversationsDays: string;
    replayImages: string;
    summaryTask: string;
    systemPrompt: string;
    inputSchema: string;
    outputSchema: string;
    params: string;
}

const EMPTY_FORM: AgentForm = {
    code: '', description: '', kind: 'DELEGATED', ownerService: '', dataClass: 'INTERNAL', status: 'ACTIVE', modelId: null,
    fallbackModelId: null, providerKeyId: null, maxOutputTokens: '2048', maxTurns: '10', runTimeoutS: '120', maxConversationRuns: '10',
    countsTowardUserBudgets: false,
    cache: 'SYSTEM', contextTools: [], webTools: EMPTY_WEB_TOOLS, contextTokens: '', replayToolResultChars: '', otherConversationsDays: '', replayImages: '', summaryTask: '',
    systemPrompt: '', inputSchema: '', outputSchema: '', params: '',
};

const CODE_PATTERN = /^[a-z0-9._-]+$/;

const TABS = [
    { key: 'prompt', label: 'Prompt & model' },
    { key: 'tools', label: 'Tools' },
    { key: 'delegation', label: 'Delegation' },
    { key: 'conversations', label: 'Conversations' },
    { key: 'effective', label: 'Effective tools' },
    { key: 'test', label: 'Test run' },
    { key: 'runs', label: 'Recent runs' },
];

/** The keys of params the Conversations tab edits; the params JSON editor shows the rest. */
const OWN_PARAM_KEYS = ['cache', 'contextTools', 'conversation', 'serverTools'];

function formFromAgent(agent: AiAgentDto): AgentForm {
    const params = (agent.params ?? {}) as Record<string, unknown>;
    const conversation = (params.conversation && typeof params.conversation === 'object' ? params.conversation : {}) as Record<string, unknown>;
    const rest = Object.fromEntries(Object.entries(params).filter(([key]) => !OWN_PARAM_KEYS.includes(key)));
    const text = (value: unknown) => (value === undefined || value === null ? '' : String(value));
    return {
        code: agent.code,
        description: agent.description ?? '',
        kind: agent.kind,
        ownerService: agent.ownerService ?? '',
        dataClass: agent.dataClass,
        status: agent.status,
        modelId: agent.modelId,
        fallbackModelId: agent.fallbackModelId,
        providerKeyId: agent.providerKeyId,
        maxOutputTokens: agent.maxOutputTokens != null ? String(agent.maxOutputTokens) : '',
        maxTurns: agent.maxTurns != null ? String(agent.maxTurns) : '',
        runTimeoutS: agent.runTimeoutS != null ? String(agent.runTimeoutS) : '',
        maxConversationRuns: agent.maxConversationRuns != null ? String(agent.maxConversationRuns) : '',
        countsTowardUserBudgets: agent.countsTowardUserBudgets ?? false,
        cache: params.cache === false ? 'OFF'
            : (typeof params.cache === 'string' && ['OFF', 'TOOLS', 'SYSTEM'].includes(params.cache.toUpperCase())
                ? params.cache.toUpperCase() as AiCacheMode : 'SYSTEM'),
        contextTools: contextToolsFromParams(params.contextTools),
        webTools: webToolsFromParams(params.serverTools),
        contextTokens: text(conversation.contextTokens),
        replayToolResultChars: text(conversation.replayToolResultChars),
        otherConversationsDays: text(conversation.otherConversationsDays),
        replayImages: text(conversation.replayImages),
        summaryTask: text(conversation.summaryTask),
        systemPrompt: agent.systemPrompt ?? '',
        inputSchema: toJsonText(agent.inputSchema),
        outputSchema: toJsonText(agent.outputSchema),
        params: toJsonText(Object.keys(rest).length > 0 ? rest : null),
    };
}

function buildRequest(form: AgentForm): { request: AiUpsertAgentRequest | null; error: string | null } {
    if (!CODE_PATTERN.test(form.code)) return { request: null, error: 'Code is required: lowercase letters, digits, ".", "_", "-".' };
    const numbers: Record<string, number | null> = {};
    for (const [key, label] of [['maxOutputTokens', 'Max output tokens'], ['maxTurns', 'Max turns'], ['runTimeoutS', 'Run timeout']] as const) {
        const value = parseOptionalNumber(form[key]);
        if (value === undefined) return { request: null, error: `${label} must be a number.` };
        numbers[key] = value;
    }
    // Blank = unlimited (null); otherwise a positive number.
    const maxConversationRuns = form.maxConversationRuns.trim() === '' ? null : parseOptionalNumber(form.maxConversationRuns);
    if (maxConversationRuns === undefined || (maxConversationRuns !== null && maxConversationRuns < 1)) {
        return { request: null, error: 'Max runs per conversation must be a positive number (or blank for unlimited).' };
    }
    const inputSchema = parseJsonText<AiJsonObject>(form.inputSchema, 'input schema');
    if (inputSchema.error) return { request: null, error: inputSchema.error };
    const outputSchema = parseJsonText<AiJsonObject>(form.outputSchema, 'output schema');
    if (outputSchema.error) return { request: null, error: outputSchema.error };
    const params = parseJsonText<AiParams>(form.params, 'params');
    if (params.error) return { request: null, error: params.error };
    // The Conversations tab's fields win over the same keys typed into the params JSON.
    const conversation: Record<string, number | string> = {};
    for (const [key, label, min] of [['contextTokens', 'Context budget', 2000], ['replayToolResultChars', 'Replayed tool result length', 200],
        ['otherConversationsDays', 'Other conversations note', 0], ['replayImages', 'Photos shown', 0]] as const) {
        if (form[key].trim() === '') continue;
        const value = parseOptionalNumber(form[key]);
        if (value === undefined || value === null || value < min) return { request: null, error: `${label} must be a number of at least ${min}.` };
        conversation[key] = value;
    }
    if (form.summaryTask.trim() !== '') conversation.summaryTask = form.summaryTask.trim();
    const merged: Record<string, unknown> = Object.fromEntries(
        Object.entries((params.value ?? {}) as Record<string, unknown>).filter(([key]) => !OWN_PARAM_KEYS.includes(key)),
    );
    merged.cache = form.cache;
    const contextTools = contextToolsToParams(form.contextTools);
    if (contextTools.error) return { request: null, error: contextTools.error };
    if (contextTools.value.length > 0) merged.contextTools = contextTools.value;
    const webTools = webToolsToParams(form.webTools);
    if (webTools.error) return { request: null, error: webTools.error };
    if (webTools.value.length > 0) merged.serverTools = webTools.value;
    if (Object.keys(conversation).length > 0) merged.conversation = conversation;
    return {
        error: null,
        request: {
            code: form.code,
            description: form.description.trim() || null,
            kind: form.kind,
            ownerService: form.ownerService.trim() || null,
            dataClass: form.dataClass,
            systemPrompt: form.systemPrompt || null,
            inputSchema: inputSchema.value,
            outputSchema: outputSchema.value,
            modelId: form.modelId,
            fallbackModelId: form.fallbackModelId,
            providerKeyId: form.providerKeyId,
            maxOutputTokens: numbers.maxOutputTokens,
            maxTurns: numbers.maxTurns,
            runTimeoutS: numbers.runTimeoutS,
            maxConversationRuns,
            params: Object.keys(merged).length > 0 ? merged as AiParams : null,
            status: form.status,
            countsTowardUserBudgets: form.countsTowardUserBudgets,
        },
    };
}

export default function AiAgentEditPage() {
    const { code: codeParam } = useParams<{ code: string }>();
    const isNew = codeParam === 'new';
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabKey = isNew ? 'prompt' : (searchParams.get('tab') ?? 'prompt');
    const tabIndex = Math.max(0, TABS.findIndex(t => t.key === tabKey));
    const catalog = useAiCatalog();
    const [agent, setAgent] = React.useState<AiAgentDto | null>(null);
    const [form, setForm] = React.useState<AgentForm>(EMPTY_FORM);
    const [saved, setSaved] = React.useState<AgentForm>(EMPTY_FORM);
    const [loading, setLoading] = React.useState(!isNew);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [confirmDelete, setConfirmDelete] = React.useState(false);

    React.useEffect(() => {
        if (isNew || !codeParam) {
            setAgent(null);
            setForm(EMPTY_FORM);
            setSaved(EMPTY_FORM);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        getAiAgent(codeParam).then(a => {
            if (cancelled) return;
            setLoading(false);
            if (a == null) {
                setLoadError(aiLoadErrorText(AI_LABELS.agent, `Failed to load agent ${codeParam}.`));
                return;
            }
            setLoadError(null);
            setAgent(a);
            const f = formFromAgent(a);
            setForm(f);
            setSaved(f);
        });
        return () => { cancelled = true; };
    }, [codeParam, isNew]);

    const patch = (p: Partial<AgentForm>) => setForm(f => ({ ...f, ...p }));
    const dirty = JSON.stringify(form) !== JSON.stringify(saved);

    const save = async () => {
        const { request, error: validation } = buildRequest(form);
        if (request == null) {
            setError(textError(validation ?? 'Invalid form'));
            return;
        }
        setSaving(true);
        setError(null);
        setNotice(null);
        const result = isNew ? await createAiAgent(request) : await updateAiAgent(codeParam ?? request.code, request);
        setSaving(false);
        if (result.error != null || result.data == null) {
            setError(toAiActionError(result, 'Save failed') ?? textError('Save failed: empty response'));
            return;
        }
        const f = formFromAgent(result.data);
        setAgent(result.data);
        setForm(f);
        setSaved(f);
        setNotice(isNew ? `Agent ${result.data.code} created — link its tools next.` : 'Saved.');
        if (isNew) navigate(`/ai/agents/${encodeURIComponent(result.data.code)}?tab=tools`, { replace: true });
    };

    const remove = async () => {
        if (!codeParam) return;
        setConfirmDelete(false);
        setSaving(true);
        const result = await deleteAiAgent(codeParam);
        setSaving(false);
        if (result.error != null) {
            setError(toAiActionError(result, result.status === 409 ? 'Cannot delete' : 'Delete failed'));
            return;
        }
        navigate('/ai/agents');
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
    }

    const agentCode = codeParam ?? '';
    return (
        <Stack gap={2} sx={{ mx: 5, my: 4 }}>
            <Breadcrumbs>
                <MuiLink component={RouterLink} to="/ai" underline="hover">AI</MuiLink>
                <MuiLink component={RouterLink} to="/ai/agents" underline="hover">Agents</MuiLink>
                <Typography color="text.primary">{isNew ? 'New agent' : codeParam}</Typography>
            </Breadcrumbs>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Box>
                    <Stack direction="row" gap={1} alignItems="center">
                        <Typography variant="h4" sx={{ fontFamily: isNew ? undefined : 'monospace' }}>{isNew ? 'New agent' : codeParam}</Typography>
                        {agent && <Chip size="small" label={agent.kind} />}
                        {(agent?.workflows ?? (agent?.workflowCode ? [{ code: agent.workflowCode, primary: true }] : [])).map(w => (
                            <Chip key={w.code} size="small" variant={w.primary ? 'filled' : 'outlined'} clickable component={RouterLink}
                                label={`workflow ${w.code}${w.primary ? ' (primary)' : ''}`}
                                to={`/ai/workflows/${encodeURIComponent(w.code)}`} />
                        ))}
                        {(agent?.params as Record<string, unknown> | null)?.serverTools != null && <Chip size="small" color="info" variant="outlined" label="web tools" />}
                        {agent?.status === 'DISABLED' && <Chip size="small" variant="outlined" label="DISABLED" />}
                    </Stack>
                    {agent && (
                        <Typography variant="caption" color="text.secondary">
                            Updated {formatAiDate(agent.updatedAt)}{agent.updatedBy ? ` by ${agent.updatedBy}` : ''} · created {formatAiDate(agent.createdAt)}
                        </Typography>
                    )}
                </Box>
                <Stack direction="row" gap={1} alignItems="center">
                    {dirty && <Chip size="small" color="warning" label="unsaved changes" />}
                    {!isNew && codeParam && (
                        <AiExportButton request={{ agents: [codeParam], includeDependencies: true }} disabled={saving || loadError != null}
                            tooltip={<>Downloads this agent with its dependencies: linked tasks and subagents (recursively), so the file imports on its own.
                                MCP servers, models and provider keys are referenced by code / name and must exist on the target.
                                {dirty ? ' Unsaved changes are not included.' : ''}</>}
                            onOutcome={o => { setError(o.error); setNotice(o.notice); }} />
                    )}
                    {!isNew && (
                        <Button color="error" variant="outlined" startIcon={<DeleteIcon />} disabled={saving} onClick={() => setConfirmDelete(true)}>
                            Delete
                        </Button>
                    )}
                    {(tabKey === 'prompt' || tabKey === 'conversations') && (
                        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            disabled={saving || (!dirty && !isNew) || loadError != null} onClick={save}>
                            {isNew ? 'Create' : 'Save'}
                        </Button>
                    )}
                </Stack>
            </Stack>
            {loadError && <Alert severity="error">{loadError}</Alert>}
            <AiErrorAlert error={error} onClose={() => setError(null)} />
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

            {loadError == null && (
                <>
                    <Tabs value={tabIndex} onChange={(_, i: number) => setSearchParams({ tab: TABS[i].key }, { replace: true })}
                        variant="scrollable" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        {TABS.map((t, i) => <Tab key={t.key} label={t.label} disabled={isNew && i > 0} />)}
                    </Tabs>

                    {tabKey === 'prompt' && (
                        <Stack gap={2}>
                            {catalog.error && <Alert severity="warning">{catalog.error}</Alert>}
                            {dirty && !isNew && <Alert severity="info">Other tabs (test run, effective tools) use the saved version.</Alert>}
                            <Card variant="outlined">
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <TextField label="Code" fullWidth required value={form.code} disabled={!isNew}
                                                onChange={e => patch({ code: e.target.value.toLowerCase() })}
                                                error={form.code !== '' && !CODE_PATTERN.test(form.code)}
                                                helperText={isNew ? 'lowercase letters, digits, ".", "_", "-" (e.g. support.betty)' : 'Code cannot be changed'} />
                                        </Grid>
                                        <Grid item xs={12} md={5}>
                                            <TextField label="Description" fullWidth value={form.description} onChange={e => patch({ description: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField label="Owner service" fullWidth value={form.ownerService} onChange={e => patch({ ownerService: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <EnumSelect label="Kind" value={form.kind} options={catalog.meta?.agentKinds ?? AI_AGENT_KINDS}
                                                onChange={v => v && patch({ kind: v })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <EnumSelect label="Data class" value={form.dataClass} options={catalog.meta?.dataClasses ?? AI_DATA_CLASSES}
                                                onChange={v => v && patch({ dataClass: v })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <EnumSelect label="Status" value={form.status} options={catalog.meta?.statuses ?? AI_STATUSES}
                                                onChange={v => v && patch({ status: v })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <TextField label="Max output tokens" fullWidth value={form.maxOutputTokens}
                                                onChange={e => patch({ maxOutputTokens: e.target.value.replace(/[^\d]/g, '') })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <TextField label="Max turns" fullWidth value={form.maxTurns}
                                                onChange={e => patch({ maxTurns: e.target.value.replace(/[^\d]/g, '') })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <TextField label="Run timeout (s)" fullWidth value={form.runTimeoutS}
                                                onChange={e => patch({ runTimeoutS: e.target.value.replace(/[^\d]/g, '') })} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={<Switch checked={form.countsTowardUserBudgets} onChange={e => patch({ countsTowardUserBudgets: e.target.checked })} />}
                                                label="Counts toward per-user budgets" />
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Only agents with this on are counted against and blocked by per-user budgets (AI › Budgets › Per user); calls without an end user are never affected.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Stack gap={2}>
                                                <AiModelPickers catalog={catalog} modelId={form.modelId} fallbackModelId={form.fallbackModelId}
                                                    providerKeyId={form.providerKeyId} onChange={p => patch(p)} />
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                            <Stack gap={2}>
                                                <AiCodeEditor label="Params (JSON)" language="json" path={`ai-agent-${agentCode}-params.json`}
                                                    value={form.params} onChange={v => patch({ params: v })} height={190} helper={PARAMS_HELP} />
                                                <AiEffectiveParams modelId={form.modelId} fallbackModelId={form.fallbackModelId}
                                                    paramsText={form.params} agentCode={isNew ? null : codeParam} />
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                            <AiCodeEditor label="System prompt" language="plaintext" path={`ai-agent-${agentCode}-system.txt`}
                                value={form.systemPrompt} onChange={v => patch({ systemPrompt: v })} height={380} />
                            <AgentPromptContext cache={form.cache} contextTools={form.contextTools} onChange={p => patch(p)} />
                            <AgentWebTools value={form.webTools} onChange={webTools => patch({ webTools })} />
                            <Grid container spacing={2}>
                                <Grid item xs={12} lg={6}>
                                    <AiCodeEditor label="Input schema (JSON Schema, optional)" language="json" path={`ai-agent-${agentCode}-input-schema.json`}
                                        value={form.inputSchema} onChange={v => patch({ inputSchema: v })} height={240}
                                        helper="Validates the run `input` object." />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <AiCodeEditor label="Output schema (JSON Schema, optional)" language="json" path={`ai-agent-${agentCode}-output-schema.json`}
                                        value={form.outputSchema} onChange={v => patch({ outputSchema: v })} height={240}
                                        helper="The final answer must match it (e.g. { response, chatLanguage })." />
                                </Grid>
                            </Grid>
                        </Stack>
                    )}
                    {!isNew && tabKey === 'tools' && (
                        <Stack gap={2}>
                            {(form.webTools.search.enabled || form.webTools.fetch.enabled) && (
                                <Alert severity="info">
                                    Also uses {[form.webTools.search.enabled && 'web search', form.webTools.fetch.enabled && 'web fetch'].filter(Boolean).join(' and ')} (run
                                    by the provider, not MCP) — set them on the Prompt & model tab.
                                </Alert>
                            )}
                            <AgentToolsTab agentCode={agentCode} />
                        </Stack>
                    )}
                    {!isNew && tabKey === 'delegation' && <AgentDelegationTab agentCode={agentCode} />}
                    {!isNew && tabKey === 'conversations' && (
                        <AgentConversationSettings form={form} patch={patch} defaults={catalog.meta?.conversationDefaults ?? null}
                            agentCode={agentCode} dirty={dirty} />
                    )}
                    {!isNew && tabKey === 'effective' && <AgentEffectiveToolsTab agentCode={agentCode} />}
                    {!isNew && tabKey === 'test' && <AgentTestRunTab agentCode={agentCode} />}
                    {!isNew && tabKey === 'runs' && <AiRunsGrid fixedAgentCode={agentCode} pageSize={10} />}
                </>
            )}

            <ConfirmDialog
                open={confirmDelete}
                title="Delete agent?"
                text={<>Agent <b>{codeParam}</b> and its links will be deleted. An agent that already has runs cannot be deleted (409) — disable it instead.</>}
                confirmLabel="Delete"
                confirmColor="error"
                onClose={() => setConfirmDelete(false)}
                onConfirm={remove}
            />
        </Stack>
    );
}
